export type BlockquoteDirective =
  | { kind: "cta"; id: string }
  | { kind: "video"; source: string }
  | { kind: "audio"; source: string; title?: string };

type HastParagraphChild = {
  type: string;
  value?: unknown;
  tagName?: unknown;
  properties?: unknown;
};

type HastParagraph = { children?: HastParagraphChild[] };

export function getBlockquoteParagraphText(pNode: HastParagraph): string {
  return (pNode.children ?? [])
    .map((child) => {
      if (child.type === "text" && typeof child.value === "string") return child.value;
      if (child.type === "element" && child.tagName === "a") {
        const href =
          typeof child.properties === "object" && child.properties !== null
            ? (child.properties as { href?: unknown }).href
            : undefined;
        return typeof href === "string" ? href : "";
      }
      return "";
    })
    .join("")
    .trim();
}

export function parseBlockquoteDirective(text: string): BlockquoteDirective | null {
  const trimmed = text.trim();
  if (trimmed.startsWith("[!CTA]")) {
    const id = trimmed.slice("[!CTA]".length).trim();
    return id ? { kind: "cta", id } : null;
  }
  if (trimmed.startsWith("[!VIDEO]")) {
    const source = trimmed.slice("[!VIDEO]".length).trim();
    return source ? { kind: "video", source } : null;
  }
  if (trimmed.startsWith("[!AUDIO]")) {
    const rest = trimmed.slice("[!AUDIO]".length).trim();
    if (!rest) return null;
    // First whitespace-delimited token is the source path; any remainder is an
    // optional human-readable title (file paths never contain spaces).
    const spaceIdx = rest.search(/\s/);
    const source = spaceIdx === -1 ? rest : rest.slice(0, spaceIdx);
    const title = spaceIdx === -1 ? undefined : rest.slice(spaceIdx + 1).trim() || undefined;
    return source ? { kind: "audio", source, title } : null;
  }
  return null;
}

export function parseBlockquoteDirectiveFromParagraph(
  pNode: HastParagraph,
): BlockquoteDirective | null {
  return parseBlockquoteDirective(getBlockquoteParagraphText(pNode));
}

// Cheap raw-markdown scans so post summaries can flag embedded media without
// shipping or rendering the full content. They mirror the [!AUDIO]/[!VIDEO]
// blockquote directives parsed above: a directive marker that opens a
// blockquote line (allowing leading indentation and nested `>`).
const AUDIO_DIRECTIVE_LINE = /^[ \t]*>[ \t>]*\[!AUDIO\]/im;
const VIDEO_DIRECTIVE_LINE = /^[ \t]*>[ \t>]*\[!VIDEO\]/im;

export function contentHasAudioDirective(content: string): boolean {
  return AUDIO_DIRECTIVE_LINE.test(content);
}

export function contentHasVideoDirective(content: string): boolean {
  return VIDEO_DIRECTIVE_LINE.test(content);
}
